import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
export class ProviderNotificationService {

  private ordersSubject = new BehaviorSubject<any[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);

  orders$ = this.ordersSubject.asObservable();
  unreadCount$ = this.unreadCountSubject.asObservable();

  private orderSub!: Subscription;
  private connSub!: Subscription;
  private started = false;

  constructor(
    private http: HttpClient,
    private socketService: SocketService
  ) {}

  startListening() {
    if (this.started) return;

    this.started = true;

    // Load existing notifications
    this.fetchOrders();

    // Refresh order list whenever backend pushes a notification
    // to the PROVIDER_NOTIFICATION_{providerId} room
    this.orderSub = this.socketService
      .onProviderNotification()
      .subscribe((data) => {

        console.log('📦 New provider notification received:', data);

        this.fetchOrders();
      });

    // Resync on every (re)connect too — same safety net as the admin
    // service: self-corrects after any missed push (join-room race,
    // brief disconnect, dropped packet) without needing a page refresh.
    this.connSub = this.socketService.connected$.subscribe(() => {
      console.log('🔄 Socket (re)connected — resyncing provider orders');
      this.fetchOrders();
    });
  }

  private fetchOrders() {

    // ⚠️ CONFIRM — this endpoint is a guess based on the 404'd request
    // seen earlier ('module/admin/provider/messages'). Verify the
    // correct path with your backend team and update if needed.
    this.http
      .get<any>(
        `${environment.API_ENDPOINT}module/admin/grocery/messages`
      )
      .subscribe({
        next: (res) => {

          console.log('Provider notification raw response:', res);

          const orders: any[] =
            Array.isArray(res?.data?.orders)
              ? res.data.orders
              : Array.isArray(res?.data?.messages)
              ? res.data.messages
              : Array.isArray(res?.data)
              ? res.data
              : [];

          console.log('Provider notifications extracted:', orders);

          const normalized = orders.map((o) => {

            const supplementary = o?.supplementary || {};

            return {

              // Notification message ID — must be sent as messageId
              // in the PUT (mark-as-read) API.
              _id: o?._id,

              orderId:
                o?.orderId ||
                supplementary?.orderId ||
                o?._id,

              orderNumber:
                o?.orderNumber ??
                supplementary?.orderNo ??
                o?.orderId ??
                'N/A',

              customerName:
                o?.customer?.fname
                  ? `${o.customer.fname} ${o.customer.lname || ''}`.trim()
                  : o?.customerName ??
                    supplementary?.customerName ??
                    'Unknown',

              customerPhone:
                o?.customerPhone ??
                supplementary?.customerPhone ??
                '',

              amount:
                o?.totalAmount ??
                o?.amount ??
                supplementary?.totalAmount ??
                0,

              status:
                supplementary?.status ??
                o?.status ??
                'pending',

              title:
                o?.title ??
                'NEW_PROVIDER_ORDER',

              body:
                o?.body ??
                '',

              createdAt:
                o?.createdAt ??
                supplementary?.createdAt,

              seen:
                o?.isRead ??
                o?.seen ??
                false,

              supplementary: supplementary
            };
          });

          console.log('✅ Normalized provider notifications:', normalized);

          this.ordersSubject.next(normalized);

          this.unreadCountSubject.next(
            normalized.filter((o) => !o.seen).length
          );
        },

        error: (err) => {
          console.error('❌ Provider notification fetch error:', err);
        }
      });
  }

  /*
   * Mark notification as READ
   *
   * ⚠️ CONFIRM this PUT path/payload shape against your backend —
   * mirrored from the admin service's pattern:
   * { messageId: notification._id }
   */
  markOrderSeen(orderId: string) {

    const selectedOrder = this.ordersSubject.value.find(
      (o) => o._id === orderId
    );

    if (!selectedOrder) {
      console.warn(
        '⚠️ Provider notification not found:',
        orderId
      );

      return;
    }

    const payload = {
      messageId: selectedOrder._id
    };

    console.log(
      '📖 Mark provider notification as read:',
      payload
    );

    // Optimistic UI update — flips to "read" immediately without
    // waiting for the API response.
    const updated = this.ordersSubject.value.map((o) =>
      o._id === selectedOrder._id
        ? {
            ...o,
            seen: true
          }
        : o
    );

    this.ordersSubject.next(updated);

    this.unreadCountSubject.next(
      updated.filter((o) => !o.seen).length
    );

    this.http
      .put<any>(
        `${environment.API_ENDPOINT}module/admin/grocery/messages`,
        payload
      )
      .subscribe({

        next: (res) => {

          console.log(
            '✅ Provider notification marked as read:',
            res
          );

        },

        error: (err) => {

          console.error(
            '❌ Failed to mark provider notification as read:',
            err
          );

          // If the API fails, reload so frontend state matches backend.
          this.fetchOrders();

        }

      });
  }

  stopListening() {

    this.orderSub?.unsubscribe();
    this.connSub?.unsubscribe();

    this.started = false;
  }
}